import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, MapPin, Phone, Send, Star } from "lucide-react";
import { toast } from "sonner";
import { SOINS, CABINETS } from "../data/content";
import { Reveal } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [params] = useSearchParams();
  const rawPreset = params.get("soin") || "";
  const preset = rawPreset.startsWith("formation-") ? "formation" : rawPreset;
  const [form, setForm] = useState({ name: "", email: "", phone: "", soin: preset, message: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      toast.success("Merci pour votre envoi ! Je vous réponds très vite.");
      setForm({ name: "", email: "", phone: "", soin: "", message: "" });
    } catch {
      toast.error("L'envoi a échoué. Réessayez dans un instant.");
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-olive-border bg-beige-50 px-4 py-3.5 text-sm text-ink placeholder:text-stone2 focus:outline-none focus:ring-2 focus:ring-olive/40 focus:border-olive transition-all";

  return (
    <div data-testid="contact-page">
      <section className="pt-32 lg:pt-40 pb-24 px-6 lg:px-12 max-w-7xl mx-auto">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-olive mb-6">Contactez-moi</p>
          <h1 className="font-serif text-5xl sm:text-6xl font-light tracking-tight text-ink leading-tight max-w-3xl">Écrivez-moi, je vous écoute</h1>
        </Reveal>

        <div className="mt-16 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <input required data-testid="contact-input-name" placeholder="Votre nom" value={form.name} onChange={set("name")} className={inputCls} />
                  <input required type="email" data-testid="contact-input-email" placeholder="Votre e-mail" value={form.email} onChange={set("email")} className={inputCls} />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <input data-testid="contact-input-phone" placeholder="Votre téléphone (facultatif)" value={form.phone} onChange={set("phone")} className={inputCls} />
                  <select data-testid="contact-select-care" value={form.soin} onChange={set("soin")} className={inputCls}>
                    <option value="">Vous me contactez pour…</option>
                    {SOINS.map((s) => (
                      <option key={s.slug} value={s.slug}>{s.name}</option>
                    ))}
                    <option value="formation">Une formation</option>
                    <option value="constellations">Une constellation (groupe ou individuelle)</option>
                    <option value="ateliers">Un atelier (breathwork, kintsugi, massage des mains)</option>
                    <option value="sejour-initiatique">Le séjour initiatique</option>
                    <option value="trek-sahara">Trek dans le Sahara — du 7 au 14 déc. 2026</option>
                    <option value="autre">Autre demande</option>
                  </select>
                </div>
                <textarea required rows={6} data-testid="contact-textarea-message" placeholder="Votre message" value={form.message} onChange={set("message")} className={inputCls} />
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-button"
                  className="inline-flex items-center gap-2 rounded-full bg-olive px-8 py-4 text-sm font-medium text-beige-100 transition-all duration-300 hover:bg-olive-dark hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0"
                >
                  {sending ? "Envoi en cours…" : "Envoyer"} <Send className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </form>
              <div className="mt-8 rounded-2xl border border-olive-border bg-beige-50 p-6 sm:p-7 flex flex-wrap items-center justify-between gap-5" data-testid="contact-google-review">
                <div className="max-w-sm">
                  <div className="flex items-center gap-1 text-gold" aria-hidden="true">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-current" strokeWidth={1.5} />
                    ))}
                  </div>
                  <p className="mt-3 text-sm text-sage leading-relaxed">
                    Vous avez déjà bénéficié d'un soin ou d'une formation ? Votre avis sur Google m'aide énormément — et il guide les personnes qui cherchent un accompagnement.
                  </p>
                </div>
                <a
                  href="https://g.page/r/CXfl1coDnhXnEAE/review"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-google-review-button"
                  className="inline-flex items-center gap-2 rounded-full border border-olive px-6 py-3 text-sm text-olive transition-all duration-300 hover:bg-olive hover:text-beige-100 hover:-translate-y-0.5"
                >
                  Laisser un avis Google
                </a>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.2}>
              <div className="bg-olive-dark text-beige-100 rounded-3xl p-8" data-testid="contact-locations">
                <p className="text-xs uppercase tracking-[0.25em] text-beige-300/80 mb-6">Où suis-je ?</p>
                <div className="space-y-6">
                  {CABINETS.map((c) => (
                    <div key={c.city} className="flex items-start gap-4">
                      <MapPin className="w-5 h-5 mt-1 shrink-0 text-beige-300" strokeWidth={1.5} />
                      <div>
                        <p className="font-serif text-2xl">{c.city}</p>
                        <p className="text-sm text-beige-300/85 mt-1">{c.address}, {c.zip}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-6 border-t border-beige-100/15 space-y-3">
                  <a href="mailto:juliezittel6@gmail.com" data-testid="contact-email-link" className="flex items-center gap-3 text-sm text-beige-200/90 hover:text-beige-100 transition-colors">
                    <Mail className="w-4 h-4 shrink-0 text-beige-300" strokeWidth={1.5} /> juliezittel6@gmail.com
                  </a>
                  <a href="tel:+33623502131" data-testid="contact-phone-link" className="flex items-center gap-3 text-sm text-beige-200/90 hover:text-beige-100 transition-colors">
                    <Phone className="w-4 h-4 shrink-0 text-beige-300" strokeWidth={1.5} /> +33 6 23 50 21 31
                  </a>
                </div>
                <p className="mt-8 pt-6 border-t border-beige-100/15 text-sm text-beige-300/85 leading-relaxed">
                  Certains soins (magnétisme, LaHoChi, ostéothérapie) peuvent également se recevoir à distance.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
